// src/lib/prayerAudience.js
// Who can see a shared prayer request, and how that list is worked out.
// Single source of truth for the Share picker (ShareSelect) and the publisher (useSharedPrayerSync).
//
//   mentor    → my mentor ("Paul") only
//   timothys  → my direct disciples (tree level TIMOTHY) who are linked app users
//   pair      → mentor + my Timothys
//   tree      → mentor + every disciple in my tree who is a linked app user
//   circle    → everyone I am linked with as a prayer partner (either direction)
//   people    → specific linked people I pick
//   public    → every signed-in user
//
// A person only counts when they are BOTH linked as a prayer partner AND (for the tree
// options) attached to a disciple entry through its `userUid`.
import { DISCIPLESHIP_LEVELS } from '@/hooks/feature/useDiscipleshipTracking';

export const MAX_AUDIENCE = 50;

export const AUDIENCE_OPTIONS = [
  { mode: 'private',  label: 'Private (only me)' },
  { mode: 'mentor',   label: 'My mentor (Paul) only' },
  { mode: 'timothys', label: 'My Timothys (direct disciples)' },
  { mode: 'pair',     label: 'Paul + my Timothys' },
  { mode: 'tree',     label: 'My whole discipleship tree' },
  { mode: 'circle',   label: 'My discipleship circle (everyone linked)' },
  { mode: 'people',   label: 'Choose specific people' },
  { mode: 'public',   label: 'Public (all app users)' },
];

/** Firestore collection that holds the shared copy for a mode, or null when private. */
export const collectionFor = (mode) => {
  if (mode === 'public') return 'publicPrayers';
  if (!mode || mode === 'private') return null;
  return 'circlePrayers';
};

/**
 * @param {string} mode
 * @param {{ linkedUids: Set<string>, mentorUid?: string, disciples?: any[], shareUids?: string[] }} ctx
 * @returns {string[]} sorted, de-duplicated uids (max MAX_AUDIENCE). Empty for private/public.
 */
export function resolveAudience(mode, ctx) {
  const linked    = ctx?.linkedUids instanceof Set ? ctx.linkedUids : new Set();
  const disciples = Array.isArray(ctx?.disciples) ? ctx.disciples : [];
  const withAcct  = (list) => list.map((d) => d?.userUid).filter((u) => u && linked.has(u));
  const mentor    = ctx?.mentorUid && linked.has(ctx.mentorUid) ? [ctx.mentorUid] : [];
  const timothys  = withAcct(disciples.filter((d) => d?.level === DISCIPLESHIP_LEVELS.TIMOTHY));

  let uids = [];
  switch (mode) {
    case 'mentor':   uids = mentor; break;
    case 'timothys': uids = timothys; break;
    case 'pair':     uids = [...mentor, ...timothys]; break;
    case 'tree':     uids = [...mentor, ...withAcct(disciples)]; break;
    case 'circle':   uids = [...linked]; break;
    case 'people':   uids = (ctx?.shareUids || []).filter((u) => linked.has(u)); break;
    default:         uids = [];
  }
  return [...new Set(uids)].sort().slice(0, MAX_AUDIENCE);
}

/** True when the request text is only the checked point names joined by commas. */
export function isLabelList(text, checklist) {
  const labels = new Set((checklist || []).map((i) => String(i?.label || '').trim().toLowerCase()));
  const parts = String(text || '').split(',').map((p) => p.trim().replace(/\.$/, '').toLowerCase()).filter(Boolean);
  return parts.length > 0 && labels.size > 0 && parts.every((p) => labels.has(p));
}